import React, { useEffect, useState, useCallback } from "react";
import { resolveMediaUrl } from "../../../utility/media";
import closeButton from '../../../assets/images/close.png';
import { useNavigate } from "react-router-dom";
import { getTechnicalSanctionsWork } from "../../../services/AppService";
import { useTranslation } from "react-i18next";

const TechnicalSanctionsWorkTypesPage = () => {
    const {t}=useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Pagination State matched with Agendas page
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Handle Search Debouncing consistent with Agendas
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    useEffect(() => {
        fetchDashboardData();
    }, [page, size, debouncedSearch]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const res = await getTechnicalSanctionsWork(debouncedSearch, page, size);
            if (res) {
                setData(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (err) {
            console.error("Failed to fetch dashboard data", err);
        } finally {
            setLoading(false);
        }
    };


    // navigating to technical sanctions plan estimate page
    const onViewEstimates = (workTypeId, workTypeName) => {
        navigate(`/smartcity/proceedings/technical-sanctions/plan-estimates/${workTypeId}`, {
            state: { workTypeName }
        });
    };
    

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            {/* Header - Consistent with Agendas layout */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">
                    {t('pageTitles.technicalSanctionsList')}
                </h4>
                <img 
                    src={closeButton} 
                    alt="Close" 
                    className="w-6 h-6 cursor-pointer" 
                    onClick={() => navigate(-1)} 
                />
            </div>

            {/* Actions Section - Matches Agendas exactly */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}:</label>
                    <select 
                        value={size} 
                        onChange={(e) => {
                            setSize(Number(e.target.value));
                            setPage(0);
                        }} 
                        className="border rounded px-2 py-1 text-sm outline-none"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <label className="text-sm">{t('common.entries')}</label>
                </div>
                <div>
                    <input 
                        type="text" 
                        placeholder={t('common.search')} 
                        value={searchTerm} 
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }} 
                        className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64" 
                    />
                </div>
            </div>

            {/* Table Section - Matches ctable style */}
            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black w-16">{t('tables.srNo')}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.workName')}</th>
                            <th className="px-3 py-2 text-center border-b border-black w-32">{t('common.total')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="3" className="text-center p-4">{t('tables.technicalSanction.loadingTechnicalSanctionWorkTypes')}</td></tr>
                        ) : data.length === 0 ? (
                            <tr><td colSpan="3" className="text-center py-4">{t('tables.technicalSanction.noTechnicalSanctionWorkTypes')}</td></tr>
                        ) : (
                            data.map((item, idx) => (
                                <tr 
                                    key={item.id} 
                                    className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
                                >
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-3">
                                            {item.workImageUrl && (
                                                <img 
                                                    src={resolveMediaUrl(item.workImageUrl)} 
                                                    alt="" 
                                                    className="w-8 h-8 rounded-full object-cover border"
                                                />
                                            )}
                                            <span>{item.workTypeName}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button 
                                            onClick={() => onViewEstimates(item.id, item.workTypeName)}
                                            disabled={item.totalEstimates === 0}
                                            className="bg-blue-50 text-blue-600 font-bold px-4 py-1 rounded-full hover:bg-blue-600 hover:text-white transition-all cursor-pointer text-xs"
                                        >
                                            {item.totalEstimates}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls - Matches Agendas layout exactly */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + data.length} {t('common.of')} {totalElements} {t('common.entries')}
                </p>
                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button 
                        className="px-3 py-1 border rounded disabled:opacity-50" 
                        onClick={() => setPage((p) => Math.max(p - 1, 0))} 
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button 
                        className="px-3 py-1 border rounded disabled:opacity-50" 
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} 
                        disabled={page === totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TechnicalSanctionsWorkTypesPage;